import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, X } from 'lucide-react';

interface RecentSearch {
  query: string;
  timestamp: number;
}

export const RecentSearches: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  useEffect(() => {
    loadRecentSearches();
  }, []);

  const loadRecentSearches = () => {
    try {
      const saved = localStorage.getItem('recent_searches');
      if (saved) {
        const searches = JSON.parse(saved) as RecentSearch[];
        const sortedSearches = searches
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 3);
        setRecentSearches(sortedSearches);
      }
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  };

  const handleSearchClick = (query: string) => {
    const encodedQuery = encodeURIComponent(query.trim().replace(/\s+/g, '+'));
    navigate(`/search/q/${encodedQuery}`);
  };

  const removeRecentSearch = (queryToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const saved = localStorage.getItem('recent_searches');
      if (saved) {
        const searches = JSON.parse(saved) as RecentSearch[];
        const updatedSearches = searches.filter(search => search.query !== queryToRemove);
        localStorage.setItem('recent_searches', JSON.stringify(updatedSearches));
        setRecentSearches(updatedSearches.slice(0, 3));
      }
    } catch (error) {
      console.error('Error removing recent search:', error);
    }
  };

  if (recentSearches.length === 0) return null;

  return (
    <div className="mt-4 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-2 mb-2.5">
        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs uppercase tracking-widest text-muted-foreground font-sans font-medium">
          {t('search.recentSearches')}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {recentSearches.map((search, index) => (
          <button
            key={`${search.query}-${search.timestamp}`}
            onClick={() => handleSearchClick(search.query)}
            className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-container-high text-on-surface text-xs font-medium hover:bg-surface-container-highest transition-colors duration-200 animate-in slide-in-from-left-2"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <span className="truncate max-w-32">{search.query}</span>
            <button
              onClick={(e) => removeRecentSearch(search.query, e)}
              className="opacity-0 group-hover:opacity-100 hover:bg-surface-container rounded-sm p-0.5 transition-opacity duration-200"
              aria-label="Remove search"
            >
              <X className="w-2.5 h-2.5 text-muted-foreground" />
            </button>
          </button>
        ))}
      </div>
    </div>
  );
};
